import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BellRing, Check, MapPinned, Satellite } from 'lucide-react';
import { Drawer, Field, LiveScreen, QueryState, RecordTable, Select, StatusPill, formatDate, formatDateTime, formatNumber, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { MonitoredBoundary } from '@/screens/parts/MonitoredBoundary';
import { RequestMonitoring } from '@/screens/parts/RequestMonitoring';
import { RequestResults } from '@/screens/parts/RequestResults';

interface MonitoredRow {
  id: string; monitoring_code: string; plot_id: string; plot_code: string; farmer_name: string | null;
  village_name: string | null; geometry_version: number; area_ha: number | null;
  monitoring_status: string; last_checked_at: string | null; observation_count: number;
}

interface EventRow {
  id: string; event_code: string | null; plot_id: string; plot_code: string | null; farmer_name: string | null;
  village_name: string | null; detected_on: string; event_type: string; change_pct: number | null;
  confidence: number | null; spatial_confidence: string | null; reason_codes: string[] | null;
  explained_by: string | null; severity: string; review_status: string; reviewed_by_name: string | null;
  reviewed_at: string | null; review_remarks: string | null; rule_version: string;
}

interface UsageRow {
  provider_key: string | null; period_month: string; requests_used: number | null;
  processing_units_used: number | null; requests_limit: number | null; processing_units_limit: number | null;
  requests_used_pct: number | null; processing_units_used_pct: number | null;
  projected_requests: number | null; projected_processing_units: number | null; quota_level: string | null;
  successful_requests: number; failed_requests: number; observations_created: number;
  snapshots_created: number; luc_events_created: number; harvest_assessments_created: number;
  plots_monitored: number;
}

const REVIEW_DECISIONS = [
  { id: 'under_review', label: 'Looking into it' },
  { id: 'explained', label: 'Explained — a declared or known activity' },
  { id: 'change_confirmed', label: 'Change confirmed on the ground' },
  { id: 'false_positive', label: 'Not a real change' },
];

const TYPE_LABEL: Record<string, string> = {
  vegetation_loss: 'Vegetation loss', vegetation_gain: 'Vegetation gain', water_gain: 'Water appeared',
  water_loss: 'Water gone', bare_soil: 'Bare soil', other: 'Other',
};

/**
 * Satellite monitoring: what the engine has flagged, and what people decided about it.
 *
 * The screen is built around one distinction. The top counter shows PROVIDER quota - requests and
 * processing units, which are two separate limits - beside PLATFORM work, which is plots,
 * observations and snapshots. They are never added together: ten thousand observations is not ten
 * thousand requests, and reading it that way is how a team talks itself into a surprise bill.
 *
 * Every event says "potential" until somebody reviews it, and the decisions a reviewer may record
 * describe the ground, not a rule. Nothing here concludes that a standard was breached.
 */
export default function SatelliteScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [stat, setStat] = useState<string | null>('open');
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState<EventRow | null>(null);
  // Reviewing many at once: one storm, one decision, one reason - not fifty identical clicks.
  const [selected, setSelected] = useState<string[]>([]);
  const [bulk, setBulk] = useState(false);
  const [decision, setDecision] = useState('under_review');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const mayReview = can(profile, 'plot.monitoring', 'update');
  // One page, request and results in tabs (owner, 2026-09-28).
  const [tab, setTab] = useState<'request' | 'results' | 'flags' | 'plots'>('request');

  const events = useRows<EventRow>('satellite_luc_events', { orderBy: { column: 'detected_on', ascending: false } });
  // What is actually being watched. The screen reported what had been FLAGGED and never what it
  // was flagged on, so "is the satellite looking at the right boundary?" had no answer here.
  const monitored = useRows<MonitoredRow>('satellite_monitoring', { orderBy: { column: 'plot_code' } });
  const drift = useQuery({
    queryKey: ['monitoring-drift'],
    queryFn: () => callRpc<Array<{ monitoring_id: string; watching_current: boolean }>>('fn_monitoring_boundary_drift'),
    enabled: isSupabaseConfigured,
  });
  const driftById = useMemo(
    () => new Map((drift.data ?? []).map((row) => [row.monitoring_id, row.watching_current])),
    [drift.data],
  );
  const [boundaryFor, setBoundaryFor] = useState<MonitoredRow | null>(null);
  const usage = useRows<UsageRow>('satellite_usage_summary', { orderBy: { column: 'period_month', ascending: false } });
  const current = (usage.data ?? [])[0];
  // High resolution is metered in AREA, not in requests or processing units, so it gets its own
  // reading rather than being folded into a meter that counts something else.
  const highRes = useQuery({
    queryKey: ['high-res-budget'],
    enabled: isSupabaseConfigured,
    queryFn: () => callRpc<{ used_ha: number | null; budget_ha: number | null; enabled: boolean }>('fn_high_res_budget'),
  });

  const stats = useMemo(() => [
    { key: 'all', label: 'All flagged', tone: 'neutral' as const, match: () => true },
    { key: 'open', label: 'Waiting on review', tone: 'warn' as const, match: (row: EventRow) => ['potential', 'under_review'].includes(row.review_status) },
    { key: 'critical', label: 'Outside a declared activity', tone: 'error' as const, match: (row: EventRow) => row.severity === 'critical' },
    { key: 'explained', label: 'Explained', tone: 'ok' as const, match: (row: EventRow) => row.review_status === 'explained' },
    { key: 'confirmed', label: 'Change confirmed', tone: 'info' as const, match: (row: EventRow) => row.review_status === 'change_confirmed' },
    { key: 'low', label: 'Low confidence', tone: 'neutral' as const, match: (row: EventRow) => row.spatial_confidence === 'low' },
  ], []);

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => applyStat(events.data ?? [], stats, stat)
    .filter((row) => !term || [row.plot_code, row.farmer_name, row.village_name, row.event_code]
      .some((value) => (value ?? '').toLowerCase().includes(term))), [events.data, stats, stat, term]);

  const columns: Array<Column<EventRow>> = [
    { key: 'detected_on', label: 'Detected', render: (row) => (
      <span className="cell-stack"><strong>{formatDate(row.detected_on)}</strong>
        <small className="muted">{row.event_code ?? ''}</small></span>) },
    { key: 'plot_code', label: 'Plot', render: (row) => (
      <span className="cell-stack"><span>{row.plot_code ?? '--'}</span>
        <small className="muted">{[row.farmer_name, row.village_name].filter(Boolean).join(' · ')}</small></span>) },
    { key: 'event_type', label: 'What changed', render: (row) => TYPE_LABEL[row.event_type] ?? row.event_type },
    { key: 'change_pct', label: 'Change', align: 'right', render: (row) => (row.change_pct === null ? '--' : `${formatNumber(row.change_pct, 1)}%`) },
    { key: 'spatial_confidence', label: 'Confidence', render: (row) => <StatusPill value={row.spatial_confidence ?? 'low'} /> },
    { key: 'severity', label: 'Severity', render: (row) => <StatusPill value={row.severity} /> },
    { key: 'review_status', label: 'Review', render: (row) => (
      <span className="cell-stack"><StatusPill value={row.review_status} />
        {row.reviewed_by_name && <small className="muted">{row.reviewed_by_name}</small>}</span>) },
    { key: 'review', label: '', render: (row) => (mayReview && ['potential', 'under_review'].includes(row.review_status) ? (
      <button className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); setReviewing(row); setDecision('under_review'); setRemarks(''); }} type="button">
        <Check size={14} /> Review
      </button>) : null) },
  ];

  async function submitReview() {
    if (!reviewing && !bulk) return;
    setSaving(true);
    try {
      if (bulk) {
        const result = await callRpc<{ reviewed: number; skipped: number }>('fn_review_luc_events', {
          p_event_ids: selected, p_decision: decision, p_remarks: remarks.trim() || null,
        });
        await queryClient.invalidateQueries({ queryKey: ['satellite_luc_events'] });
        setBulk(false);
        setSelected([]);
        toast.success(`${result.reviewed} recorded${result.skipped ? `, ${result.skipped} skipped` : ''}.`);
        return;
      }
      await callRpc('fn_review_luc_event', { p_event_id: reviewing!.id, p_decision: decision, p_remarks: remarks.trim() || null });
      await queryClient.invalidateQueries({ queryKey: ['satellite_luc_events'] });
      setReviewing(null);
      toast.success('Recorded.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <LiveScreen subtitle="What the satellite has flagged on monitored plots, and what was decided about it."
                title="Satellite monitoring">
      <div className="tab-row" role="tablist">
        {([['request', 'Request'], ['results', 'Requests & results'], ['flags', 'Flagged changes'], ['plots', 'Monitored plots & usage']] as const).map(([id, label]) => (
          <button aria-selected={tab === id} className={tab === id ? 'active' : ''} key={id} onClick={() => setTab(id)} role="tab" type="button">{label}</button>
        ))}
      </div>

      {tab === 'request' && (
        <RequestMonitoring monitoredPlotIds={(monitored.data ?? []).filter((row) => row.monitoring_status !== 'closed').map((row) => row.plot_id)}
                           onRequested={() => setTab('results')} />
      )}

      {tab === 'results' && <RequestResults />}

      {tab === 'plots' && (<>
      {/* What is being watched, before what was flagged: a reader asking "is it looking at the
          right shape?" should not have to read the alerts to find out. */}
      <section className="card">
        <div className="section-heading">
          <h3><MapPinned size={15} /> Monitored plots</h3>
          {driftById.size > 0 && [...driftById.values()].some((ok) => !ok) && (
            <span className="badge badge-amber">
              <AlertTriangle size={12} /> a boundary has changed since monitoring started
            </span>
          )}
        </div>
        <QueryState empty="No plot is being monitored yet." query={monitored}>
          <RecordTable
            columns={[
              { key: 'plot_code', label: 'Plot', render: (row) => <strong>{row.plot_code}</strong> },
              { key: 'farmer_name', label: 'Farmer', render: (row) => row.farmer_name ?? '--' },
              { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
              { key: 'area_ha', label: 'Area', align: 'right', render: (row) => `${formatNumber(row.area_ha, 3)} ha` },
              {
                key: 'geometry_version',
                label: 'Boundary',
                render: (row) => (driftById.get(row.id) === false
                  ? <span className="badge badge-amber"><AlertTriangle size={12} /> v{row.geometry_version}, plot has moved on</span>
                  : <span className="muted">v{row.geometry_version}</span>),
              },
              { key: 'monitoring_status', label: 'Status', render: (row) => <StatusPill value={row.monitoring_status} /> },
              { key: 'last_checked_at', label: 'Last checked', render: (row) => formatDateTime(row.last_checked_at) },
            ]}
            onRowClick={(row) => setBoundaryFor(row)}
            rows={monitored.data ?? []}
          />
        </QueryState>
      </section>

      <Drawer onClose={() => setBoundaryFor(null)} open={Boolean(boundaryFor)}
              subtitle="The shape the satellite compares each new image against."
              title={boundaryFor ? `Boundary watched for ${boundaryFor.plot_code}` : 'Boundary'} wide>
        {boundaryFor && <MonitoredBoundary monitoringId={boundaryFor.id} />}
      </Drawer>

      {current && (
        <section className="card sat-usage">
          <div className="section-heading">
            <h3><Satellite size={15} /> This month's usage</h3>
            <StatusPill value={current.quota_level ?? 'none'} />
          </div>
          <div className="sat-usage-grid">
            <Meter label="API requests" used={current.requests_used} limit={current.requests_limit}
                   pct={current.requests_used_pct} projected={current.projected_requests} />
            <Meter label="Processing units" used={current.processing_units_used} limit={current.processing_units_limit}
                   pct={current.processing_units_used_pct} projected={current.projected_processing_units} />
            {highRes.data?.enabled && (
              <Meter label="High-resolution area (ha)" limit={highRes.data.budget_ha}
                     pct={highRes.data.budget_ha ? Math.min(100, ((highRes.data.used_ha ?? 0) / highRes.data.budget_ha) * 100) : null}
                     projected={null} used={highRes.data.used_ha} />
            )}
            <dl className="sat-counts">
              <div><dt>Plots monitored</dt><dd>{formatNumber(current.plots_monitored)}</dd></div>
              <div><dt>Observations</dt><dd>{formatNumber(current.observations_created)}</dd></div>
              <div><dt>Monthly snapshots</dt><dd>{formatNumber(current.snapshots_created)}</dd></div>
              <div><dt>Flagged changes</dt><dd>{formatNumber(current.luc_events_created)}</dd></div>
              <div><dt>Harvest assessments</dt><dd>{formatNumber(current.harvest_assessments_created)}</dd></div>
              <div><dt>Failed calls</dt><dd>{formatNumber(current.failed_requests)}</dd></div>
            </dl>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: '12.5px' }}>
            Requests and processing units are separate limits, and neither is the same as the work above:
            one request is not one processing unit, and an observation is not a request.
            {highRes.data?.enabled
              ? ' High-resolution imagery is metered differently again, by area: with no budget configured, none is bought.'
              : ''}
          </p>
        </section>
      )}

      </>)}

      {tab === 'flags' && (<>
      <StatCards active={stat} onSelect={setStat} rows={events.data ?? []} stats={stats} />
      <QueryState empty="Nothing flagged yet." query={events}>
        <DataGrid bulkActions={mayReview ? (ids) => (
                    <button className="primary" onClick={() => { setSelected(ids); setBulk(true); setDecision('under_review'); setRemarks(''); }} type="button">
                      <Check size={14} /> Review {ids.length} together
                    </button>) : undefined}
                  columns={columns} exportName="satellite-events" onSearchChange={setSearch}
                  onSelectionChange={mayReview ? setSelected : undefined} rows={visible}
                  screenKey="plot.monitoring" search={search} searchPlaceholder="Plot, farmer, village, code"
                  selectable={mayReview ? (row) => ['potential', 'under_review'].includes(row.review_status) : undefined}
                  selectedIds={selected} />
      </QueryState>
      </>)}

      <Drawer footer={
        <>
          <button className="cancel" onClick={() => { setReviewing(null); setBulk(false); }} type="button">Cancel</button>
          <button className="primary" disabled={saving} onClick={() => void submitReview()} type="button">
            {saving ? 'Recording...' : 'Record'}
          </button>
        </>
      } onClose={() => { setReviewing(null); setBulk(false); }} open={Boolean(reviewing) || bulk}
        subtitle="What the satellite saw is evidence to check, not a finding. Say what the review found."
        title={bulk ? `Review ${selected.length} changes together` : reviewing ? `Review ${reviewing.event_code ?? 'change'}` : 'Review'}>
        {bulk && (
          <>
            <p className="muted">
              The same decision and the same remarks will be recorded against {selected.length} changes.
              Anything you cannot see, or that needs its own explanation, is skipped and stays open.
            </p>
            <Field hint="A declared or known activity explains a fall; field records settle the rest." label="What did the review find?" required>
              <Select onChange={setDecision} options={REVIEW_DECISIONS} value={decision} />
            </Field>
            <Field label="Remarks" required={decision !== 'under_review'}>
              <textarea onChange={(event) => setRemarks(event.target.value)} rows={3}
                        placeholder="e.g. Cyclone on 18 Sep: all five villages affected, verified on 24 Sep." value={remarks} />
            </Field>
          </>
        )}
        {!bulk && reviewing && (
          <>
            <dl className="detail-grid">
              <div><dt>Plot</dt><dd>{reviewing.plot_code ?? '--'}</dd></div>
              <div><dt>Detected</dt><dd>{formatDate(reviewing.detected_on)}</dd></div>
              <div><dt>What changed</dt><dd>{TYPE_LABEL[reviewing.event_type] ?? reviewing.event_type}</dd></div>
              <div><dt>Change</dt><dd>{reviewing.change_pct === null ? '--' : `${formatNumber(reviewing.change_pct, 1)}%`}</dd></div>
              <div><dt>Spatial confidence</dt><dd><StatusPill value={reviewing.spatial_confidence ?? 'low'} /></dd></div>
              <div><dt>Why it was flagged</dt>
                <dd>{(reviewing.reason_codes ?? []).map((code) => code.replace(/_/g, ' ')).join(', ') || '--'}</dd></div>
              <div><dt>Rule version</dt><dd>{reviewing.rule_version}</dd></div>
            </dl>
            <Field hint="A declared harvest or silviculture explains a fall; field records settle the rest." label="What did the review find?" required>
              <Select onChange={setDecision} options={REVIEW_DECISIONS} value={decision} />
            </Field>
            <Field label="Remarks" required={decision !== 'under_review'}>
              <textarea onChange={(event) => setRemarks(event.target.value)} rows={3}
                        placeholder="e.g. Visited 24 Sep: thinning done under the approved plan, not a clearance." value={remarks} />
            </Field>
          </>
        )}
      </Drawer>
    </LiveScreen>
  );
}

/** One quota meter. A limit nobody set reads as "not set", never as unlimited. */
function Meter({ label, used, limit, pct, projected }: {
  label: string; used: number | null; limit: number | null; pct: number | null; projected: number | null;
}) {
  const share = pct === null ? null : Math.min(pct, 100);
  return (
    <div className="sat-meter">
      <span className="sat-meter-label">{label}</span>
      <strong>{used === null ? 'none reported' : formatNumber(used, used % 1 === 0 ? 0 : 2)}</strong>
      <small className="muted">{limit === null ? 'no limit configured' : `of ${formatNumber(limit)}`}</small>
      <div aria-hidden className="sat-meter-bar"><span style={{ width: `${share ?? 0}%` }} /></div>
      {/* With no limit set there is no percentage to report, and a bare "--" before the projection
          read as a missing number rather than as a limit nobody configured. */}
      <small className="muted">
        {[
          pct === null ? null : `${formatNumber(pct, 1)}% used`,
          projected === null ? null : `${formatNumber(projected, 0)} projected by month end`,
        ].filter(Boolean).join(' · ') || 'nothing to project yet'}
      </small>
    </div>
  );
}

export function SatelliteAlertsBadge({ count }: { count: number }) {
  return <span className="sat-badge"><BellRing size={13} /> {count}</span>;
}

export const SATELLITE_TIME_FORMAT = formatDateTime;
